import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { EnvironmentService } from '@lib/core/environment';
import { Uuid } from '@lib/core/type';
import { ComponentLeaveProtectorService } from '@proget-shared/component-leave-protector';
import { ConfigurableDialog, DialogModule, DialogService } from '@proget-shared/dialog';
import { ConditionalFormGeneratorService, ConditionalFormGroup, FormValueMask } from '@proget-shared/form/conditional-form-generator';
import { FileSelectModule } from '@proget-shared/form/file-select';
import { FormControlErrorModule } from '@proget-shared/form/form-control-error';
import { FormControlLabelModule } from '@proget-shared/form/form-control-label';
import { FormHelperModule } from '@proget-shared/form/helper';
import { PasswordModule } from '@proget-shared/form/password';
import { ResponseErrorForm, ResponseErrorTemplateMapper } from '@proget-shared/helper/response-error';
import { TranslateModule } from '@proget-shared/translate';
import { SpinnerModule } from '@proget-shared/ui/spinner';
import { ToastService } from '@proget-shared/ui/toast';
import { filter, finalize, noop } from 'rxjs';

import { UserCertificatesRepository } from '../user-certificates.repository';

@Component({
  selector: 'lib-user-certificates-form-dialog',
  templateUrl: './user-certificates-form-dialog.component.html',
  styleUrls: ['./user-certificates-form-dialog.component.scss'],
  providers: [ConditionalFormGeneratorService],
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SpinnerModule,
    DialogModule,
    FormControlLabelModule,
    FormControlErrorModule,
    FileSelectModule,
    PasswordModule,
    FormHelperModule,
    DialogModule,
  ],
})
export class UserCertificatesFormDialogComponent implements ConfigurableDialog {
  protected readonly form: ConditionalFormGroup;
  protected readonly maxCertificateFileSize: number;

  protected errors: ResponseErrorForm = {};
  protected requestPending = false;
  protected loadingData = true;
  protected certificateId: Uuid | null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly toastService = inject(ToastService);
  private readonly userCertificatesRepository = inject(UserCertificatesRepository);
  private readonly componentLeaveProtector = inject(ComponentLeaveProtectorService);
  private readonly conditionalFormGeneratorService = inject(ConditionalFormGeneratorService);
  private readonly environment = inject(EnvironmentService);

  private userId: Uuid;

  constructor() {
    this.maxCertificateFileSize = this.environment.maxFileUploadSize.certificates;

    this.form = this.conditionalFormGeneratorService.generate({
      encryption: {
        children: {
          selected: { value: false },
          uploaded: { value: false },
          file: { value: null },
          password: { value: '', conditions: [`.file:${FormValueMask.TRUTHY}:enable`] },
        },
      },
      signing: {
        children: {
          selected: { value: false },
          uploaded: { value: false },
          file: { value: null },
          password: { value: '', conditions: [`.file:${FormValueMask.TRUTHY}:enable`] },
        },
      },
    });

    this.form.get('encryption.file').valueChanges
      .pipe(
        filter((file) => !!file),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.form.get('encryption.selected').setValue(true);
        },
      });

    this.form.get('signing.file').valueChanges
      .pipe(
        filter((file) => !!file),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.form.get('signing.selected').setValue(true);
        },
      });

    this.componentLeaveProtector.protect(this.form);

    this.destroyRef.onDestroy(() => {
      this.componentLeaveProtector.stopProtectingData();
    });
  }

  onConfiguration(configuration: any): void {
    this.userId = configuration?.userId ?? configuration?.items[0]?.uuid;

    if (this.userId) {
      this.loadUserCertificate(this.userId);
    }
  }

  protected close(): void {
    this.dialogService.reject(this);
  }

  protected isEncryptionCertificateSelected(): boolean {
    return this.form.get('encryption.selected').value;
  }

  protected isSigningCertificateSelected(): boolean {
    return this.form.get('signing.selected').value;
  }

  protected removeEncryptionCertificate(): void {
    this.form.get('encryption.selected').reset(false);
    this.form.get('encryption.file').reset();
  }

  protected removeSigningCertificate(): void {
    this.form.get('signing.selected').reset(false);
    this.form.get('signing.file').reset();
  }

  protected submit(): void {
    if (this.form.invalid || this.requestPending) {
      return;
    }

    this.requestPending = true;

    if (this.certificateId && !this.isEncryptionCertificateSelected() && !this.isSigningCertificateSelected()) {
      this.deleteUserCertificate();

      return;
    }

    this.certificateId ? this.updateUserCertificate() : this.createUserCertificate();
  }

  private createUserCertificate(): void {
    this.userCertificatesRepository.createUserSMIMECertificate(
      this.userId,
      this.form.value.encryption,
      this.form.value.signing
    )
      .subscribe({
        next: () => {
          this.toastService.success('library.user_card.toast.user_certificates_form.success');
          this.form.markAsPristine();
          this.dialogService.resolve(this);
        },
        error: ({ form, messages }) => {
          this.errors = form ?? {};
          this.requestPending = false;
          this.toastService.error('library.user_card.toast.user_certificates_form.error', ResponseErrorTemplateMapper.map(messages));
        },
      });
  }

  private loadUserCertificate(userId: Uuid): void {
    if (!userId) {
      this.loadingData = false;

      return;
    }

    this.userCertificatesRepository.getUserSMIMECertificate(userId)
      .pipe(finalize(() => {
        this.loadingData = false;
      }))
      .subscribe({
        next: (certificate) => {
          this.certificateId = certificate?.id;

          if (certificate) {
            this.form.patchValue({
              certificateId: certificate.id,
              encryption: {
                selected: !!certificate.encryptionCertificate,
                uploaded: !!certificate.encryptionCertificate,
              },
              signing: {
                selected: !!certificate.signingCertificate,
                uploaded: !!certificate.signingCertificate,
              },
            });
          }
        },
        error: noop,
      });
  }

  private updateUserCertificate(): void {
    this.userCertificatesRepository.updateUserSMIMECertificate(
      this.certificateId,
      this.form.value.encryption,
      this.form.value.signing
    )
      .subscribe({
        next: () => {
          this.toastService.success('library.user_card.toast.user_certificates_form.success');
          this.form.markAsPristine();
          this.dialogService.resolve(this);
        },
        error: ({ form, messages }) => {
          this.errors = form ?? {};
          this.requestPending = false;
          this.toastService.error('library.user_card.toast.user_certificates_form.error', ResponseErrorTemplateMapper.map(messages));
        },
      });
  }

  private deleteUserCertificate(): void {
    this.userCertificatesRepository.deleteUserCertificate(this.certificateId)
      .subscribe({
        next: () => {
          this.toastService.success('library.user_card.toast.user_certificates_form.success');
          this.form.markAsPristine();
          this.dialogService.resolve(this);
        },
        error: ({ form, messages }) => {
          this.errors = form ?? {};
          this.requestPending = false;
          this.toastService.error('library.user_card.toast.user_certificates_form.error', ResponseErrorTemplateMapper.map(messages));
        },
      });
  }
}
